import { Rgb, Scene, WallpaperStyleInfo, darken, lighten, luminance, mix, offscreen, prng, rgba, share, upscale, valueNoise } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'forest', label: 'Forest',
  blurb: 'Pine forest silhouettes in layers of morning mist',
  params: [ share( 'trees', 'Trees', 50 ), share( 'mist', 'Mist', 55 ) ],
};

/**
 * Forest: ranks of conifer silhouettes standing on gentle hills, back to front, each rank one
 * filled path and each followed by a band of fog pooling at its feet. The trees
 * are vectors at full size (40 to 200 of them, about 30 points each, so 5K costs what Full HD
 * does); the only per pixel work is the small sky canvas that is scaled up.
 */
export function draw( scene: Scene ): void {
  const { ctx, w, h, box, colors, rand, p, seed } = scene;
  const white: Rgb = { r: 255, g: 255, b: 255 };
  // the tiles' order: the first tile is the sky and the mist; the rest are the tree
  // ranks walked back to front, and a single tree tile is spread over three ranks by the mist alone
  const sky = colors[ 0 ];
  const tiles = colors.slice( 1 );
  const ranks = tiles.length > 1 ? tiles : [ 0, 1, 2 ].map( () => tiles[ 0 ] || darken( sky, 0.55 ) );
  const n = ranks.length;
  // the fog is the sky's colour lifted toward white, the darker the sky the more: a fog of the
  // plain sky colour is invisible on a night palette, where the ranks are dark on dark
  const lum = luminance( sky );
  const haze = lighten( sky, 0.14 + 0.3 * ( 1 - lum ) );
  // just under the canvas, where every rank's path is closed so the fill has no seam at the bottom edge
  const floor = h + 2;

  // The sky (the vertical gradient, darkened 15% at the zenith and lighter at the horizon) is
  // computed on a small canvas and scaled up: a full size gradient over a thousand rows steps
  // visibly on a dark palette, and one level of dither per pixel down here dissolves the steps
  // for free
  const k = Math.min( 480 / w, 270 / h );
  const sw = Math.max( 2, Math.round( w * k ) ), sh = Math.max( 2, Math.round( h * k ) );
  const small = offscreen( sw, sh );
  const img = small.ctx.createImageData( sw, sh );
  const data = img.data;
  const dither = prng( seed ^ 0x5cd1 );
  const zenith = darken( sky, 0.15 );
  const horizon = lighten( sky, 0.1 );
  const hy = box.y + box.h * 0.92;
  for ( let y = 0; y < sh; y++ ) {
    const v = Math.max( 0, Math.min( 1, ( ( y + 0.5 ) / k - box.y ) / ( hy - box.y ) ) );
    // the ramp is kept in floats, so the dither below really picks between two levels
    const cr = zenith.r + ( horizon.r - zenith.r ) * v;
    const cg = zenith.g + ( horizon.g - zenith.g ) * v;
    const cb = zenith.b + ( horizon.b - zenith.b ) * v;
    for ( let x = 0; x < sw; x++ ) {
      const n = dither() - 0.5;
      const o = ( y * sw + x ) * 4;
      data[ o ] = cr + n;
      data[ o + 1 ] = cg + n;
      data[ o + 2 ] = cb + n;
      data[ o + 3 ] = 255;
    }
  }
  small.ctx.putImageData( img, 0, 0 );
  upscale( ctx, small.canvas, w, h );

  // Trees: 0 = a few tall pines standing apart, 100 = a dense wall of small ones
  const heightScale = 1.25 - p[ 0 ] * 0.6;
  const spacing = 1.4 - p[ 0 ] * 1.05;
  // Mist: how far each rank dissolves into the sky and how thick the fog at its feet is
  const mistStrength = 0.2 + p[ 1 ] * 0.7;
  const bandAlpha = 0.12 + p[ 1 ] * 0.6;
  const bandH = box.h * ( 0.03 + p[ 1 ] * 0.06 );
  // the ground lines run up to 92% of the box height at the front and as high as 44% at the back,
  // so the forest fills the frame and the sky is a band over it, not half the picture; a small
  // number of ranks keeps a step of at most 20% so the back one is not lost behind the front
  const step = Math.min( 0.48 / Math.max( 1, n - 1 ), 0.2 );
  const backLevel = 0.92 - ( n - 1 ) * step;
  const spread = Math.max( 0.001, 0.92 - backLevel );
  const bands: number[] = [];

  for ( let i = 0; i < n; i++ ) {
    const t = n > 1 ? i / ( n - 1 ) : 1;
    // the ground lines are not evenly spaced: equal steps leave equal slabs of colour between the
    // ranks, which is what reads as bands rather than as distance
    const level = 0.92 - ( n - 1 - i ) * step + ( i > 0 && i < n - 1 ? ( rand() - 0.5 ) * step * 0.55 : 0 );
    // the trees grow with their rank's place: 8% of the box at the back, 30% at the front
    const depth = ( level - backLevel ) / spread;
    const rankH = box.h * ( 0.08 + depth * 0.22 ) * heightScale;
    const ground = valueNoise( rand, 3 );
    const hill = valueNoise( rand, 3 );
    // the ground rolls enough for the rank's edge to read as a hill: flat lines stacked back to
    // front are what would turn this into striped bands
    const amp = box.h * ( 0.018 + depth * 0.032 );
    const gy = ( x: number ) => box.y + box.h * level + ( ground( ( x - box.x ) / box.w, 0.5 ) - 0.5 ) * 2 * amp;
    const margin = rankH;

    ctx.beginPath();
    // the ground first: one subpath the trunks run into, so trees and hill fill as one shape
    // (every subpath is traced clockwise, the nonzero rule then unions the overlapping trees)
    ctx.moveTo( -margin, gy( -margin ) );
    const segs = 240;
    for ( let s = 1; s <= segs; s++ ) {
      const x = -margin + ( w + margin * 2 ) * s / segs;
      ctx.lineTo( x, gy( x ) );
    }
    ctx.lineTo( w + margin, floor );
    ctx.lineTo( -margin, floor );
    ctx.closePath();

    const unit = rankH * 0.62 * spacing;
    // The rank's own trees, the ones inside the design box, come from the main stream, so the wood
    // is the same at every size; the canvas beyond the box is planted from a generator of its own,
    // which leaves that stream untouched (owner, 2026-09-26: an ultrawide is more of the SAME
    // picture, not another one)
    const plant = ( x: number, g: () => number ) => {
      conifer( ctx, x, gy( x ), rankH * ( 0.72 + g() * 0.56 ) * ( 0.74 + 0.52 * hill( ( x - box.x ) / box.w, 0.5 ) ), rankH, g, floor, box.w / 1920 );
    };
    const boxLeft = box.x - margin, boxRight = box.x + box.w + margin;
    for ( let x = boxLeft + rand() * unit; x < boxRight; x += unit * ( 0.6 + rand() * 0.8 ) ) plant( x, rand );
    const side = prng( ( Math.imul( seed ^ 0x7ee5, 2654435761 ) + i * 9176 ) >>> 0 );
    for ( let x = boxLeft - unit * ( 0.6 + side() * 0.8 ); x > -margin; x -= unit * ( 0.6 + side() * 0.8 ) ) plant( x, side );
    for ( let x = boxRight + unit * ( 0.6 + side() * 0.8 ); x < w + margin; x += unit * ( 0.6 + side() * 0.8 ) ) plant( x, side );
    // flat, so the silhouette stays a silhouette: the tile mixed toward the fog by how far back the
    // rank stands, the front one its own colour. The mist band below does the dissolving.
    const line = box.y + box.h * level;
    ctx.fillStyle = rgba( mix( ranks[ i ], haze, mistStrength * ( 1 - t ) ), 1 );
    ctx.fill();

    // the fog: clear above the rank's feet, thickest at its ground line and clear again just under
    // it, so it pools in a band between this rank and the next instead of hazing the whole floor
    fog( ctx, w, haze, line, bandH, bandAlpha );
    bands.push( line );
  }

  // the same bands once more over the finished picture, weaker: a bank of mist is a level sheet of
  // air, so it passes in front of the nearer ranks too, and without this second pass every band is
  // hidden behind the trees of the rank in front of it
  for ( const line of bands ) fog( ctx, w, haze, line, bandH * 1.15, bandAlpha * 0.4 );
}

/** one bank of mist lying on a ground line: clear above, thickest at the line, clear again below it */
function fog( ctx: CanvasRenderingContext2D, w: number, haze: Rgb, line: number, bandH: number, alpha: number ): void {
  const top = line - bandH;
  const band = ctx.createLinearGradient( 0, top, 0, line + bandH * 0.7 );
  band.addColorStop( 0, rgba( haze, 0 ) );
  band.addColorStop( 1 / 1.7, rgba( haze, alpha ) );
  band.addColorStop( 1, rgba( haze, 0 ) );
  ctx.fillStyle = band;
  ctx.fillRect( 0, top, w, bandH * 1.7 );
}

/**
 * One conifer as a clockwise subpath: a short trunk, 5 to 9 tiers of drooping branches whose width
 * shrinks toward the tip, every corner and notch jittered on each side on its own so no two trees
 * match, closed under the canvas so it joins the ground subpath.
 */
function conifer( ctx: CanvasRenderingContext2D, xc: number, groundY: number, height: number, rankH: number, rand: () => number, floor: number, unitPx: number ): void {
  // the lowest tier ends under the hill, by its own amount per tree, so the rank behind cannot show
  // through as a dashed line along the ground and the bottom edges do not all align
  const base = groundY + height * ( 0.02 + rand() * 0.04 );
  // narrow spires next to broad firs: the width and the taper are drawn per tree, not from the height alone
  const halfW = height * ( 0.19 + rand() * 0.12 );
  const taper = 0.72 + rand() * 0.5;
  // the taller the tree, the more tiers; five at least so it still reads as a pine at phone scale
  const tiers = Math.min( 9, 5 + Math.floor( rand() * 2 ) + ( height > rankH * 1.02 ? 1 : 0 ) + ( height > rankH * 1.18 ? 1 : 0 ) + ( height > rankH * 1.32 ? 1 : 0 ) );
  // under 1 the tiers crowd toward the tip, the way a fir's branches do
  const curve = 0.74 + rand() * 0.26;
  const lean = ( rand() - 0.5 ) * halfW * 0.25;
  // the tier bottoms from the ground to the apex
  const by: number[] = [];
  const f: number[] = [];
  for ( let k = 0; k <= tiers; k++ ) {
    let v = Math.pow( k / tiers, curve );
    if ( k > 0 && k < tiers ) v += ( rand() - 0.5 ) * 0.4 / tiers;
    f.push( v );
    by.push( base - height * v );
  }
  const wl: number[] = [], wr: number[] = [];
  for ( let k = 0; k < tiers; k++ ) {
    const ww = halfW * Math.pow( 1 - f[ k ], taper );
    wl.push( ww * ( 0.84 + rand() * 0.32 ) );
    wr.push( ww * ( 0.84 + rand() * 0.32 ) );
  }
  // the notch where a tier meets the one above sits inside the upper tier's width, and that
  // tier's corner hangs a little below the notch: the zigzag that says conifer
  const nl: number[] = [], nr: number[] = [], droop: number[] = [];
  for ( let k = 0; k < tiers - 1; k++ ) {
    nl.push( wl[ k + 1 ] * ( 0.45 + rand() * 0.2 ) );
    nr.push( wr[ k + 1 ] * ( 0.45 + rand() * 0.2 ) );
    droop.push( ( by[ k ] - by[ k + 1 ] ) * ( 0.12 + rand() * 0.2 ) );
  }
  const trunk = Math.max( halfW * 0.1, 0.6 * unitPx );
  ctx.moveTo( xc - trunk, floor );
  ctx.lineTo( xc - trunk, by[ 0 ] );
  for ( let k = 0; k < tiers; k++ ) {
    ctx.lineTo( xc - wl[ k ], by[ k ] );
    if ( k < tiers - 1 ) ctx.lineTo( xc - nl[ k ], by[ k + 1 ] - droop[ k ] );
  }
  ctx.lineTo( xc + lean, by[ tiers ] );
  for ( let k = tiers - 1; k >= 0; k-- ) {
    if ( k < tiers - 1 ) ctx.lineTo( xc + nr[ k ], by[ k + 1 ] - droop[ k ] );
    ctx.lineTo( xc + wr[ k ], by[ k ] );
  }
  ctx.lineTo( xc + trunk, by[ 0 ] );
  ctx.lineTo( xc + trunk, floor );
  ctx.closePath();
}
